<template>
  <v-dialog :model-value="statusType !== null" width="600" @close="statusType = null" persistent>
    <v-form>
      <v-card v-if="statusType">
        <v-card-title class="d-flex flex-row ma-2">
          <h4 class="text-capitalize" data-testid="yard-spot-status-dialog-title">
            {{
              t('yard.dialogs.yardViewSpotStatusDialog.headers.actionThisSpot', {
                action: t(`global.basicTerms.${statusType.toLowerCase()}`)
              })
            }}
          </h4>
          <v-spacer></v-spacer>
          <v-btn icon="mdi-close" variant="text" density="comfortable" @click="closeDialog"></v-btn>
        </v-card-title>
        <v-card-text>
          <div class="d-flex flex-column gap-6">
            <p class="typography-body-md" v-if="statusType === 'close'">
              {{
                t(
                  'yard.dialogs.yardViewSpotStatusDialog.messages.thisSpotWillRemainUnavailableForAssetAssignmentUntilItIsSetAsOpenAgain'
                )
              }}
            </p>
            <p class="typography-body-md" v-else-if="statusType === 'open'">
              {{
                t(
                  'yard.dialogs.yardViewSpotStatusDialog.messages.thisDoorWillBeSetAsOpenAndWillBeAvailableAgainForAssetAssignment'
                )
              }}
            </p>

            <template v-if="statusType === 'close'">
              <div>
                <strong>
                  {{ t('yard.dialogs.yardViewSpotStatusDialog.labels.reason') }}
                </strong>
                <v-autocomplete
                  color="primary"
                  v-model="reason"
                  :placeholder="
                    t('yard.dialogs.yardViewSpotStatusDialog.placeholders.selectAReason')
                  "
                  :items="[
                    {
                      title: t('yard.dialogs.yardViewSpotStatusDialog.dropDownItems.cleaning'),
                      value: 'Cleaning'
                    },
                    {
                      title: t(
                        'yard.dialogs.yardViewSpotStatusDialog.dropDownItems.underMaintenance'
                      ),
                      value: 'Under Maintenance'
                    },
                    { title: 'Blocked by Equipment', value: 'Blocked by Equipment' },
                    {
                      title: t(
                        'yard.dialogs.yardViewSpotStatusDialog.dropDownItems.safetyInspection'
                      ),
                      value: 'Safety Inspection'
                    },
                    {
                      title: t(
                        'yard.dialogs.yardViewSpotStatusDialog.dropDownItems.routineInspection'
                      ),
                      value: 'Routine Inspection'
                    },
                    {
                      title: t('yard.dialogs.yardViewSpotStatusDialog.dropDownItems.reserved'),
                      value: 'Reserved'
                    },
                    {
                      title: t('yard.dialogs.yardViewSpotStatusDialog.dropDownItems.repairsNeeded'),
                      value: 'Repairs Needed'
                    },
                    {
                      title: t('yard.dialogs.yardViewSpotStatusDialog.dropDownItems.damage'),
                      value: 'Damage'
                    }
                  ]"
                  data-testid="yard-spot-status-dialog-reason-input"
                  variant="outlined"
                  density="compact"
                  autofocus
                  required
                  clearable
                  data-allow-mismatch></v-autocomplete>
              </div>
              <div>
                <strong>
                  {{ t('yard.dialogs.yardViewSpotStatusDialog.labels.additionalNotes') }}
                </strong>

                <v-textarea
                  v-model="observation"
                  color="primary"
                  data-testid="yard-spot-status-dialog-observation-input"
                  :placeholder="`${t(
                    'yard.dialogs.yardViewSpotStatusDialog.placeholders.enterAditionalNotes'
                  )}...`"
                  variant="outlined"
                  density="compact"
                  clearable></v-textarea>
              </div>
            </template>
          </div>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn
            variant="outlined"
            @click="closeDialog"
            data-testid="yard-spot-status-dialog-cancel">
            {{ t('global.buttons.nevermind') }}
          </v-btn>
          <v-btn
            class="bg-primary"
            @click="submit"
            :disabled="statusType === 'close' && !reason"
            data-testid="yard-spot-status-dialog-submit">
            {{ t('global.buttons.confirm') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-form>
  </v-dialog>
</template>

<script>
import { defineComponent, ref } from 'vue';
import useYard from '@/modules/yard/composables/useYard';
import { useEventHub } from '@/composables';
import { useI18n } from 'vue-i18n';

export default defineComponent({
  name: 'YardViewSpotStatusDialog',
  props: {
    spot: {
      type: Object,
      required: true
    },
    statusType: {
      type: [String, null],
      default: null
    }
  },
  setup(props) {
    const { t } = useI18n();
    const yardStore = useYard();
    const eventHub = useEventHub();

    const reason = ref('');
    const observation = ref('');

    const submit = () => {
      if (props.statusType === 'close') {
        yardStore.closeSpot(props.spot.id, {
          reason: reason.value,
          observation: observation.value
        });
      } else {
        yardStore.openSpot(props.spot.id, { reason: 'Opened from yard view' });
      }

      yardStore.trackMixpanelEvent(
        props.statusType === 'close'
          ? yardStore.MixPanelEvent.CLOSE_SPOT
          : yardStore.MixPanelEvent.OPEN_SPOT,
        {
          spot: props.spot,
          metadata: {
            close_spot_reason: reason.value || null
          }
        }
      );
      closeDialog();
    };

    const closeDialog = () => {
      reason.value = '';
      observation.value = '';
      eventHub.$emit('close-spot-status-dialog');
    };

    return {
      t,
      submit,
      closeDialog,
      reason,
      observation
    };
  }
});
</script>
